/**
 * The media library's scan-queue health (docs/05-admin/24 §3.4): why uploads were rejected, and
 * how long the oldest one has waited for a scan.
 *
 * **Built from the spec, not a design** (owner ruling 2026-10-26). `MediaBody` draws the counts as
 * tiles and nothing more; the reason grouping and the backlog age are the doc's layout, set out
 * with the console's own components. Both come with the library page (`GET /v1/admin/media`), so
 * the panel and the tiles above it describe the same moment.
 *
 * The backlog line is the server's (`scan_backlog_alert_minutes`, the evidence backlog alert's
 * 30 minutes), not a number typed here: a stuck scan pipeline is invisible media.
 */
import { DataTable } from '../components/data';
import { Section } from '../components/kit';
import { StatusChip } from '../components/primitives';
import type { MediaLibraryPage } from '../lib/api/hooks/media';

/** How ops would say each `reject_reason` aloud; an unknown code is shown as the server sent it. */
export const REJECT_REASONS: Record<string, string> = {
  infected: 'Virus scan hit',
  content_mismatch: 'Bytes are not the declared type',
  content_type_mismatch: 'Bytes are not the declared type',
  over_size_cap: 'Over the size cap',
  pdf_active_content: 'PDF with active content',
  unreadable: 'File could not be read',
  unrecorded: 'Reason not recorded',
};

/** Whole minutes between `since` and `now`, never negative. */
export function minutesSince(since: string, now: number): number {
  return Math.max(0, Math.floor((now - Date.parse(since)) / 60_000));
}

export function ScanHealth({ page, now = Date.now() }: { page: MediaLibraryPage; now?: number }) {
  const reasons = Object.entries(page.rejected_by_reason ?? {}).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const oldest = page.oldest_scanning_at ?? null;
  if (reasons.length === 0 && oldest === null) return null;

  const line = page.scan_backlog_alert_minutes;
  const waited = oldest === null ? 0 : minutesSince(oldest, now);
  const stuck = oldest !== null && waited >= line;

  return (
    <Section title="Scan-queue health" note={`Backlog alert line: ${line} minutes`} pad={false}>
      <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center', padding: 'var(--sp-sm) var(--sp-md)' }}>
        {oldest === null ? (
          <StatusChip tone="success" label="Nothing waiting for a scan" />
        ) : (
          <>
            <StatusChip
              tone={stuck ? 'error' : 'warn'}
              label={stuck ? `Oldest scan waiting ${waited} min — past the line` : `Oldest scan waiting ${waited} min`}
            />
            {stuck && <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>Is the virus scanner up?</span>}
          </>
        )}
      </div>
      {reasons.length > 0 && (
        <>
          <DataTable
            density="compact"
            columns={[
              { key: 'reason', label: 'Rejected because' },
              { key: 'code', label: 'Code', mono: true },
              { key: 'count', label: 'Objects', align: 'right', mono: true },
            ]}
            rows={reasons.map(([code, count]) => ({
              id: code,
              reason: REJECT_REASONS[code] ?? code,
              code,
              count: String(count),
            }))}
          />
          <p style={{ margin: 0, padding: 'var(--sp-sm) var(--sp-md)', fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
            Re-upload a rejected file from the page that uses it. The rejected row stays for audit.
          </p>
        </>
      )}
    </Section>
  );
}
